import {
  classifyInboxMessage,
  messageSearchText,
  type InboxMessageCategory,
  type InboxMessageDisplayInput,
} from "../../../../common/inboxMessageClassification.ts";

// N4-4: the classifier now lives in common/ so the worker can reuse the exact
// same rule for the red-dot badge. Re-export it for this file's existing UI
// consumers (Inbox, Message, LeagueDashboard).
export {
  classifyInboxMessage,
  type InboxMessageCategory,
  type InboxMessageDisplayInput,
};

export type InboxMessageDecisionKind = "decision" | "update" | "record";
export type InboxMessageDecisionUrgency = "critical" | "soon" | "info" | "none";
export type InboxMessageFilter = "tasks" | "unread" | "all";
export type InboxMessageQueueGroup = "mustDecide" | "unreadUpdates" | "history";
export type InboxMessageDecisionContext = {
  action: "openDecision" | "readUpdate" | "viewRecord";
  category: InboxMessageCategory;
  consequence: "decisionRisk" | "unreadContext" | "contextOnly" | "recordOnly";
  deadline: "beforeContinue" | "whenReady" | "none";
  kind: InboxMessageDecisionKind;
  priorityRank: number;
  urgency: InboxMessageDecisionUrgency;
};
export type InboxMessageQueueSection = {
  key: InboxMessageQueueGroup;
  messages: InboxMessageDisplayInput[];
};
export type InboxMessageBusinessAction = {
  defaultLabel: string;
  labelKey: string;
  path: string[];
};

export const getInboxCategoryCounts = (messages: InboxMessageDisplayInput[]) => {
  const counts: Record<InboxMessageCategory, number> = {
    completed: 0,
    info: 0,
    task: 0,
    unread: 0,
  };

  for (const message of messages) {
    counts[classifyInboxMessage(message)] += 1;
  }

  return counts;
};

const inboxCategoryPriority = {
  task: 0,
  unread: 1,
  info: 2,
  completed: 3,
} satisfies Record<InboxMessageCategory, number>;

const inboxQueueGroups = ["mustDecide", "unreadUpdates", "history"] as const;

const inboxQueueGroupPriority = {
  mustDecide: 0,
  unreadUpdates: 1,
  history: 2,
} satisfies Record<InboxMessageQueueGroup, number>;

export const getInboxMessageDecisionContext = (
  message: InboxMessageDisplayInput,
): InboxMessageDecisionContext => {
  const category = classifyInboxMessage(message);

  switch (category) {
    case "task":
      return {
        action: "openDecision",
        category,
        consequence: "decisionRisk",
        deadline: "beforeContinue",
        kind: "decision",
        priorityRank: inboxCategoryPriority[category],
        urgency: "critical",
      };
    case "unread":
      return {
        action: "readUpdate",
        category,
        consequence: "unreadContext",
        deadline: "beforeContinue",
        kind: "update",
        priorityRank: inboxCategoryPriority[category],
        urgency: "soon",
      };
    case "completed":
      return {
        action: "viewRecord",
        category,
        consequence: "recordOnly",
        deadline: "none",
        kind: "record",
        priorityRank: inboxCategoryPriority[category],
        urgency: "none",
      };
    case "info":
      return {
        action: "viewRecord",
        category,
        consequence: "contextOnly",
        deadline: "whenReady",
        kind: "record",
        priorityRank: inboxCategoryPriority[category],
        urgency: "info",
      };
  }
};

export const getInboxMessageQueueGroup = (
  message: InboxMessageDisplayInput,
): InboxMessageQueueGroup => {
  const category = classifyInboxMessage(message);

  if (category === "task") {
    return "mustDecide";
  }
  if (category === "unread") {
    return "unreadUpdates";
  }

  return "history";
};

export const defaultInboxFilter = (_messages: InboxMessageDisplayInput[]): InboxMessageFilter =>
  "all";

export const filterInboxMessages = (
  messages: InboxMessageDisplayInput[],
  filter: InboxMessageFilter,
) => {
  if (filter === "tasks") {
    return messages.filter((message) => classifyInboxMessage(message) === "task");
  }

  if (filter === "unread") {
    return messages.filter((message) => !message.read);
  }

  return [...messages].sort((a, b) => {
    const aContext = getInboxMessageDecisionContext(a);
    const bContext = getInboxMessageDecisionContext(b);

    return aContext.priorityRank - bContext.priorityRank;
  });
};

export const getInboxDecisionQueueSections = (
  messages: InboxMessageDisplayInput[],
): InboxMessageQueueSection[] => {
  const sections: Record<InboxMessageQueueGroup, InboxMessageDisplayInput[]> = {
    history: [],
    mustDecide: [],
    unreadUpdates: [],
  };

  for (const message of messages) {
    sections[getInboxMessageQueueGroup(message)].push(message);
  }

  return inboxQueueGroups
    .map((key) => ({
      key,
      messages: sections[key],
    }))
    .sort((a, b) => inboxQueueGroupPriority[a.key] - inboxQueueGroupPriority[b.key]);
};

export const getInboxMessageBusinessAction = (
  message: InboxMessageDisplayInput,
): InboxMessageBusinessAction => {
  const searchText = messageSearchText(message);

  if (/\b(contract|negotiate|re-sign|resign)\b/i.test(searchText)) {
    return {
      defaultLabel: "Go to negotiations",
      labelKey: "message.businessActionNegotiation",
      path: ["negotiation"],
    };
  }

  if (/\btrade\b/i.test(searchText)) {
    return {
      defaultLabel: "Open trade desk",
      labelKey: "message.businessActionTrade",
      path: ["trade"],
    };
  }

  if (/\b(protect|expansion)\b/i.test(searchText)) {
    return {
      defaultLabel: "Open protection list",
      labelKey: "message.businessActionProtection",
      path: ["protect_players"],
    };
  }

  if (/\b(draft|pick)\b/i.test(searchText)) {
    return {
      defaultLabel: "Go to draft",
      labelKey: "message.businessActionDraft",
      path: ["draft"],
    };
  }

  if (/\b(luxury|salary|tax|finance)\b/i.test(searchText)) {
    return {
      defaultLabel: "Review finances",
      labelKey: "message.businessActionFinances",
      path: ["team_finances"],
    };
  }

  return {
    defaultLabel: "Return to task center",
    labelKey: "message.businessActionHome",
    path: [],
  };
};

export const getInboxHomeSummary = (messages: InboxMessageDisplayInput[], limit = 3) =>
  filterInboxMessages(messages, "all")
    .filter((message) => !message.read)
    .slice(0, limit);
